import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { MdHowToReg, MdRefresh } from 'react-icons/md';

import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import EmptyState from '../../../shared/layout/feedback/EmptyState';
import MainButton from '../../../shared/layout/buttons/MainButton';
import StandardModal from '../../../shared/layout/modals/StandardModal';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';
import { StyledInput, StyledLabel, FormGroup } from '../../../shared/layout/forms/FormElements';
import {
    approveOrganization,
    fetchApprovalPlans,
    fetchOrganizationApprovals,
    rejectOrganization,
} from '../../../services/Http/AdminApi';
import { fetchOrganizationTypes } from '../../../services/Http/Api';

/**
 * Red za odobravanje registriranih tvrtki.
 *
 * Odobrenje nije puko "da": ovdje se potvrđuje plan i tip. Registrant oboje
 * samo deklarira — netko traži Growth a dogovoren je Business, netko se
 * prijavi kao proizvođač a zapravo je trgovac.
 */
const AdminOrganizationApprovals = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [plans, setPlans] = useState([]);
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const [approving, setApproving] = useState(null);
    const [planCode, setPlanCode] = useState('');
    const [typeId, setTypeId] = useState('');
    const [taxVerified, setTaxVerified] = useState(false);

    const [rejecting, setRejecting] = useState(null);
    const [reason, setReason] = useState('');

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([fetchOrganizationApprovals(), fetchApprovalPlans(), fetchOrganizationTypes()])
            .then(([queue, planList, typeList]) => {
                setRows(queue?.data ?? []);
                setPlans(planList ?? []);
                setTypes(typeList ?? []);
                setError(null);
            })
            .catch(() => setError(t('Approvals.loadFailed')))
            .finally(() => setLoading(false));
    }, [t]);

    useEffect(load, [load]);

    const openApprove = (row) => {
        setApproving(row);
        setPlanCode(row.requested_plan_code ?? 'free');
        setTypeId(String(row.requested_type_id ?? row.organization_type_id ?? ''));
        setTaxVerified(Boolean(row.tax_id_verified));
    };

    const submitApprove = () => {
        setBusyId(approving.id);
        approveOrganization(approving.id, {
            plan_code: planCode || null,
            organization_type_id: typeId ? Number(typeId) : null,
            tax_id_verified: taxVerified,
        })
            .then(() => {
                toast.success(t('Approvals.approved'));
                setApproving(null);
                load();
            })
            .catch(() => toast.error(t('Approvals.actionFailed')))
            .finally(() => setBusyId(null));
    };

    const submitReject = () => {
        if (reason.trim().length < 3) {
            toast.error(t('Approvals.reasonRequired'));
            return;
        }

        setBusyId(rejecting.id);
        rejectOrganization(rejecting.id, reason.trim())
            .then(() => {
                toast.success(t('Approvals.rejected'));
                setRejecting(null);
                setReason('');
                load();
            })
            .catch(() => toast.error(t('Approvals.actionFailed')))
            .finally(() => setBusyId(null));
    };

    const typeName = (id) => types.find((x) => Number(x.id) === Number(id))?.name ?? '—';

    const columns = [
        { name: t('Approvals.company'), selector: (row) => row.name, sortable: true, grow: 2 },
        {
            name: t('Approvals.status'),
            cell: (row) => <Badge $verified={row.status === 'verified'}>{t(`Approvals.status_${row.status}`)}</Badge>,
        },
        {
            name: t('Approvals.requestedType'),
            cell: (row) => typeName(row.requested_type_id ?? row.organization_type_id),
        },
        { name: t('Approvals.requestedPlan'), selector: (row) => row.requested_plan_code ?? '—' },
        {
            name: t('Approvals.taxId'),
            cell: (row) => (
                <span>
                    {row.tax_id ?? '—'}
                    {row.tax_id_verified ? ' ✓' : ''}
                </span>
            ),
        },
        {
            name: t('Approvals.registered'),
            selector: (row) => (row.created_at ? new Date(row.created_at).toLocaleDateString('hr-HR') : '—'),
        },
        {
            name: '',
            cell: (row) => (
                <Actions>
                    <MainButton type="button" disabled={busyId === row.id} onClick={() => openApprove(row)}>
                        {t('Approvals.approve')}
                    </MainButton>
                    <GhostButton type="button" disabled={busyId === row.id} onClick={() => setRejecting(row)}>
                        {t('Approvals.reject')}
                    </GhostButton>
                </Actions>
            ),
            grow: 2,
        },
    ];

    if (loading) return <LoadingMessage />;
    if (error) return <ErrorMessage>{error}</ErrorMessage>;

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle icon={<MdHowToReg size={28} />}>{t('Approvals.title')}</PageTitle>
                <MainButton type="button" onClick={load}>
                    <MdRefresh /> {t('Approvals.refresh')}
                </MainButton>
            </PageHeader>

            <PageContent>
                {rows.length === 0 ? (
                    <EmptyState>{t('Approvals.empty')}</EmptyState>
                ) : (
                    <DataTable columns={columns} data={rows} highlightOnHover striped />
                )}
            </PageContent>

            {approving && (
                <StandardModal
                    isOpen
                    title={`${t('Approvals.approve')}: ${approving.name}`}
                    onClose={() => setApproving(null)}
                >
                    <FormGroup>
                        <StyledLabel>{t('Approvals.plan')}</StyledLabel>
                        <SimpleSelect value={planCode} onChange={(e) => setPlanCode(e.target.value)}>
                            {plans.map((plan) => (
                                <option key={plan.code} value={plan.code}>
                                    {plan.name} {plan.price_cents > 0 ? `(${plan.price_cents / 100} ${plan.currency})` : ''}
                                </option>
                            ))}
                        </SimpleSelect>
                    </FormGroup>

                    <FormGroup>
                        <StyledLabel>{t('Approvals.confirmType')}</StyledLabel>
                        <SimpleSelect value={typeId} onChange={(e) => setTypeId(e.target.value)}>
                            {types.map((type) => (
                                <option key={type.id} value={type.id}>{type.name}</option>
                            ))}
                        </SimpleSelect>
                        <Hint>{t('Approvals.typeHint')}</Hint>
                    </FormGroup>

                    <FormGroup>
                        <CheckboxRow>
                            <StyledInput
                                type="checkbox"
                                checked={taxVerified}
                                onChange={(e) => setTaxVerified(e.target.checked)}
                            />
                            <span>{t('Approvals.taxVerified')}</span>
                        </CheckboxRow>
                    </FormGroup>

                    <ModalActions>
                        <GhostButton type="button" onClick={() => setApproving(null)}>
                            {t('Approvals.cancel')}
                        </GhostButton>
                        <MainButton type="button" disabled={busyId === approving.id} onClick={submitApprove}>
                            {t('Approvals.approve')}
                        </MainButton>
                    </ModalActions>
                </StandardModal>
            )}

            {rejecting && (
                <StandardModal
                    isOpen
                    title={`${t('Approvals.reject')}: ${rejecting.name}`}
                    onClose={() => { setRejecting(null); setReason(''); }}
                >
                    <FormGroup>
                        <StyledLabel>{t('Approvals.reason')}</StyledLabel>
                        <StyledInput
                            as="textarea"
                            rows={4}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder={t('Approvals.reasonPlaceholder')}
                        />
                        <Hint>{t('Approvals.reasonHint')}</Hint>
                    </FormGroup>

                    <ModalActions>
                        <GhostButton type="button" onClick={() => { setRejecting(null); setReason(''); }}>
                            {t('Approvals.cancel')}
                        </GhostButton>
                        <MainButton type="button" disabled={busyId === rejecting.id} onClick={submitReject}>
                            {t('Approvals.reject')}
                        </MainButton>
                    </ModalActions>
                </StandardModal>
            )}
        </PageContainer>
    );
};

const Actions = styled.div`
    display: flex;
    gap: 0.5rem;
`;

const GhostButton = styled.button`
    background: transparent;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 6px;
    padding: 0.45rem 0.9rem;
    cursor: pointer;
    font-size: 0.85rem;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const Badge = styled.span`
    display: inline-block;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    font-size: 0.75rem;
    background: ${({ $verified }) => ($verified ? 'rgba(40, 167, 69, 0.12)' : 'rgba(255, 193, 7, 0.15)')};
`;

const Hint = styled.p`
    margin: 0.35rem 0 0;
    font-size: 0.78rem;
    opacity: 0.65;
`;

const CheckboxRow = styled.label`
    display: flex;
    align-items: center;
    gap: 0.5rem;

    input {
        width: auto;
    }
`;

const ModalActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 1.25rem;
`;

export default AdminOrganizationApprovals;
